import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import { StopType, StopTimeType } from "types/MultiStop";

interface PickupAndDeliverySelectorProps {
  watchRequestFormValues: (key?: string) => any;
}

function formatStopDateTime(stop: any, intl: ReturnType<typeof useIntl>): string {
  const timeType = stop.stop_time_type;

  if (timeType === StopTimeType.ASAP_DELIVERY) {
    return intl.formatMessage({
      id: "new-request.schedule.defaultCargoReadyDeliveryOption.asap",
      defaultMessage: "ASAP Delivery",
    });
  }

  if (timeType === StopTimeType.AFTER && stop.stop_after_date && stop.stop_after_time) {
    return moment(`${stop.stop_after_date} ${stop.stop_after_time}`, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm");
  }

  if (timeType === StopTimeType.BEFORE && stop.stop_before_date && stop.stop_before_time) {
    return moment(`${stop.stop_before_date} ${stop.stop_before_time}`, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm");
  }

  if (timeType === StopTimeType.BETWEEN) {
    const after =
      stop.stop_after_date && stop.stop_after_time
        ? moment(`${stop.stop_after_date} ${stop.stop_after_time}`, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm")
        : "";
    const before =
      stop.stop_before_date && stop.stop_before_time
        ? moment(`${stop.stop_before_date} ${stop.stop_before_time}`, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, HH:mm")
        : "";
    if (after && before) return `${after} - ${before}`;
    return after || before;
  }

  return "";
}

function StopOptionLabel({ stop, stopNumber }: { stop: any; stopNumber: number }) {
  const intl = useIntl();
  const dateTime = formatStopDateTime(stop, intl);
  return (
    <div className="pickup-delivery-option">
      <div className="pickup-delivery-option__title">Stop #{stopNumber}</div>
      {stop.address_name && <div className="pickup-delivery-option__address-name">{stop.address_name}</div>}
      <div className="pickup-delivery-option__address">
        {stop.street_address}
        {stop.street_address && (stop.country_name || stop.zipcode) ? ", " : ""}
        {stop.country_name} {stop.zipcode}
      </div>
      {dateTime && <div className="pickup-delivery-option__date">{dateTime}</div>}
    </div>
  );
}

function StopAddressDetail({ stop }: { stop: any }) {
  const intl = useIntl();
  const dateTime = formatStopDateTime(stop, intl);
  const cityLine = [stop.zipcode, stop.city_name, stop.state_name, stop.country_name].filter(Boolean).join(", ");

  return (
    <div className="pickup-delivery-address-detail">
      {stop.address_name && <p className="mb-0">{stop.address_name}</p>}
      {stop.street_address && <p className="mb-0">{stop.street_address}</p>}
      {cityLine && <p className="mb-0">{cityLine}</p>}
      {dateTime && (
        <>
          <p className="mb-0">&nbsp;</p>
          <p className="mb-0">{dateTime}</p>
        </>
      )}
    </div>
  );
}

export default function PickupAndDeliverySelector({ watchRequestFormValues }: PickupAndDeliverySelectorProps) {
  const { setValue, watch } = useFormContext();
  const stops: any[] = watchRequestFormValues("schedule.stops") || [];

  const pickupStopKey = watch("pickup_stop_key");
  const deliveryStopKey = watch("delivery_stop_key");

  const emptyOption = { label: "No stop", value: "" };

  const pickupOptions = [
    emptyOption,
    ...stops
      .map((stop: any, index: number) => ({ stop, originalIndex: index }))
      .filter(({ stop }) => stop.stop_type === StopType.PICKUP)
      .map(({ stop, originalIndex }) => ({
        label: <StopOptionLabel stop={stop} stopNumber={originalIndex + 1} />,
        value: stop.stop_key,
      })),
  ];

  const deliveryOptions = [
    emptyOption,
    ...stops
      .map((stop: any, index: number) => ({ stop, originalIndex: index }))
      .filter(({ stop }) => stop.stop_type === StopType.DROPOFF)
      .map(({ stop, originalIndex }) => ({
        label: <StopOptionLabel stop={stop} stopNumber={originalIndex + 1} />,
        value: stop.stop_key,
      })),
  ];

  // Only render when there are multi-stop stops available
  if (!stops.length || (pickupOptions.length <= 1 && deliveryOptions.length <= 1)) {
    return null;
  }

  const getSelectedStop = (stopId: string, stopType: StopType) => {
    if (!stopId) return null;
    const index = stops.findIndex((s: any) => s.stop_key === stopId && s.stop_type === stopType);
    if (index === -1) return null;
    return { stop: stops[index], index };
  };

  const isMissingStop = (stopId: string, stopType: StopType) => !!stopId && !getSelectedStop(stopId, stopType);

  const getSelectedStopLabel = (stopId: string) => {
    const selected = getSelectedStop(stopId, StopType.PICKUP) || getSelectedStop(stopId, StopType.DROPOFF);
    if (!selected) return stopId ? "Missing stop" : "";
    const name = selected.stop.address_name || selected.stop.street_address || "";
    return `Stop #${selected.index + 1}${name ? ` - ${name}` : ""}`;
  };

  const selectedPickup = getSelectedStop(pickupStopKey, StopType.PICKUP);
  const selectedDelivery = getSelectedStop(deliveryStopKey, StopType.DROPOFF);

  return (
    <div className="pickup-delivery-selector">
      {pickupOptions.length > 1 && (
        <div className="pickup-delivery-selector__field">
          <label>
            <FormattedMessage id="new-request.packing-list.pickup" defaultMessage="Pickup" />
          </label>
          <Dropdown
            className="schedule-dropdown"
            dropdownClassName="pickup-delivery-dropdown"
            options={pickupOptions}
            value={pickupStopKey || ""}
            onChange={(o: any) => setValue("pickup_stop_key", o.value)}
            placeholder="Select pickup stop"
            usePortal
            customLabel={({ value, isActive, hasError }: any) => (
              <input
                type="text"
                className={`font-b3 form-control bg-white br-10 pt-6 pb-6 pr-20 w-100 height-auto placeholder-light ${
                  isActive ? "search-select-input" : "dropdown-hover"
                } ${hasError ? "invalid-input" : ""} ${
                  isMissingStop(value, StopType.PICKUP) ? "missing-pickup-delivery-text" : ""
                }`}
                value={isMissingStop(value, StopType.PICKUP) ? "Missing stop" : getSelectedStopLabel(value)}
                placeholder="Select pickup stop"
                readOnly
              />
            )}
          />
          {selectedPickup && <StopAddressDetail stop={selectedPickup.stop} />}
        </div>
      )}
      <div className="pickup-delivery-selector__field">
        <label>
          <FormattedMessage id="new-request.packing-list.delivery" defaultMessage="Delivery" />
        </label>
        <Dropdown
          className="schedule-dropdown"
          dropdownClassName="pickup-delivery-dropdown"
          options={deliveryOptions}
          value={deliveryStopKey || ""}
          onChange={(o: any) => setValue("delivery_stop_key", o.value)}
          placeholder="Select delivery stop"
          usePortal
          customLabel={({ value, isActive, hasError }: any) => (
            <input
              type="text"
              className={`font-b3 form-control bg-white br-10 pt-6 pb-6 pr-20 w-100 height-auto placeholder-light ${
                isActive ? "search-select-input" : "dropdown-hover"
              } ${hasError ? "invalid-input" : ""} ${
                isMissingStop(value, StopType.DROPOFF) ? "missing-pickup-delivery-text" : ""
              }`}
              value={isMissingStop(value, StopType.DROPOFF) ? "Missing stop" : getSelectedStopLabel(value)}
              placeholder="Select delivery stop"
              readOnly
            />
          )}
        />
        {selectedDelivery && <StopAddressDetail stop={selectedDelivery.stop} />}
      </div>
    </div>
  );
}
